import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@stitches/react';

// 区块容器样式
const SectionContainer = styled('section', {
  width: '100%',
  padding: 'var(--spacing-xxl) 0',
  backgroundColor: 'var(--background-color)'
});

// 内容容器样式
const ContentContainer = styled('div', {
  maxWidth: 'var(--container-width)',
  margin: '0 auto',
  padding: '0 var(--spacing-lg)'  
});

// 区块标题样式
const SectionTitle = styled('h2', {
  fontSize: 'var(--font-size-xxl)',
  fontWeight: 'var(--font-weight-bold)',
  textAlign: 'center',
  marginBottom: 'var(--spacing-md)',
  color: 'var(--text-primary)'
});

// 区块描述样式
const SectionDescription = styled('p', {
  fontSize: 'var(--font-size-md)',
  textAlign: 'center',
  maxWidth: '800px',
  margin: '0 auto var(--spacing-xl)',
  color: 'var(--text-secondary)'
});

// 合作伙伴容器样式
const PartnersGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gap: 'var(--spacing-lg)',
  alignItems: 'center',
  
  '@media (max-width: 992px)': {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 'var(--spacing-md)'
  },
  
  '@media (max-width: 480px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 'var(--spacing-sm)'
  }
});

// 合作伙伴Logo样式
const PartnerLogo = styled('a', {
  backgroundColor: 'var(--surface-color)',
  borderRadius: 'var(--border-radius-md)',
  padding: 'var(--spacing-md)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '120px',
  boxShadow: 'var(--shadow-sm)',
  transition: 'transform var(--transition-normal), box-shadow var(--transition-normal)',
  textDecoration: 'none',
  overflow: 'hidden',
  
  '@media (max-width: 992px)': {
    height: '100px',
    padding: 'var(--spacing-sm)'
  },
  
  '@media (max-width: 480px)': {
    height: '90px',
    padding: 'var(--spacing-xs)'
  },
  
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: 'var(--shadow-md)'
  }
});

// Logo图片样式
const LogoImage = styled('img', {
  maxWidth: '100%',
  maxHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
  transition: 'opacity var(--transition-fast)',
  filter: 'brightness(0.9)',
  
  '&:hover': {
    filter: 'brightness(1)',
  }
});

// 加载占位符样式
const LoadingPlaceholder = styled('div', {
  width: '80px',
  height: '40px',
  backgroundColor: 'var(--gray-200)',
  borderRadius: 'var(--border-radius-sm)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-secondary)',
  animation: 'pulse 1.5s ease-in-out infinite alternate',
  
  '@keyframes pulse': {
    '0%': { opacity: 0.6 },
    '100%': { opacity: 1 }
  }
});

// 错误占位符样式
const ErrorPlaceholder = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  color: 'var(--text-secondary)',
  fontSize: 'var(--font-size-sm)',
  textAlign: 'center',
  
  '@media (max-width: 480px)': {
    fontSize: 'var(--font-size-xs)'
  }
});

const ErrorText = styled('span', {
  fontWeight: 'var(--font-weight-medium)',
  marginBottom: '4px'
});

const ErrorSubtext = styled('span', {
  fontSize: 'var(--font-size-xs)',
  opacity: 0.7
});

/**
 * Logo图片组件，带有加载状态和错误处理
 */
interface LogoImageWithFallbackProps {
  src: string;
  alt: string;
  name: string;
}

const LogoImageWithFallback: React.FC<LogoImageWithFallbackProps> = ({ src, alt, name }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  if (error) {
    return (
      <ErrorPlaceholder>
        <ErrorText>{name}</ErrorText>
        <ErrorSubtext>Partner</ErrorSubtext>
      </ErrorPlaceholder>
    );
  }

  return (
    <>
      {loading && <LoadingPlaceholder>Loading...</LoadingPlaceholder>}
      <LogoImage
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        style={{ display: loading ? 'none' : 'block' }}
      />
    </>
  );
};

/**
 * 合作伙伴部分组件
 * 展示平台的合作伙伴和生态系统
 */
const PartnersSection: React.FC = () => {
  const { t } = useTranslation();
  
  // 合作伙伴数据（行业领先的挖矿、能源、区块链基础设施和钱包服务提供商）
  const partners = [
    { 
      id: 1, 
      name: 'Riot Platforms', 
      logo: '/images/partners/RIot_Logo.png',
      url: 'https://riotplatforms.com'
    },
    { 
      id: 2, 
      name: '比特大陆 (Bitmain)', 
      logo: '/images/partners/比特大陆-removebg-preview.png',
      url: 'https://bitmain.com'
    },
    { 
      id: 3, 
      name: 'Marathon Digital Holdings', 
      logo: '/images/partners/Marathon logo.png',
      url: 'https://marathondh.com'
    },
    { 
      id: 4, 
      name: 'Dominion Energy', 
      logo: '/images/partners/Dominion_Energy_logo.png',
      url: 'https://dominionenergy.com'
    },
    { 
      id: 5, 
      name: 'K-Electric Ltd', 
      logo: '/images/partners/Energy That Moves.png',
      url: 'https://ke.com.pk'
    },
    { 
      id: 6, 
      name: 'OKX Web3 Wallet', 
      logo: '/images/partners/OK wallet logo.png',
      url: 'https://okx.com'
    },
    { 
      id: 7, 
      name: 'TokenPocket', 
      logo: '/images/partners/TokenPocket.png',
      url: 'https://tokenpocket.pro'
    },
    { 
      id: 8, 
      name: 'MetaMask', 
      logo: '/images/partners/MetaMask.png',
      url: 'https://metamask.io'
    },
    { 
      id: 9, 
      name: 'Partner Network', 
      logo: '/images/partners/EDzwqC-UwAE.png',
      url: 'https://www.ankr.com/'
    },
    { 
      id: 10, 
      name: 'Binance', 
      logo: '/images/partners/Binance_logo.svg.png',
      url: 'https://www.binance.com/'
    }
  ];
  
  return (
    <SectionContainer>
      <ContentContainer>
        <SectionTitle>{t('home.partners.title')}</SectionTitle>
        <SectionDescription>
          {t('home.partners.description')}
        </SectionDescription>
        
        <PartnersGrid>
          {partners.map(partner => (
            <PartnerLogo 
              key={partner.id} 
              href={partner.url} 
              target="_blank" 
              rel="noopener noreferrer" 
              title={partner.name}
            >
              <LogoImageWithFallback
                src={partner.logo}
                alt={partner.name}
                name={partner.name.split(' ')[0]}
              />
            </PartnerLogo>
          ))}
        </PartnersGrid>
      </ContentContainer>
    </SectionContainer>
  );
};

export default PartnersSection; 